import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const AutoComplete = ({
  options,
  selected,
  setSelected,
  placeholder,
  inputClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  selectedOptionClassName = "",
  noResultsClassName = "",
  wrapperClassName = "",
}) => {
  const { t } = useTranslation("common");
  const shownPlaceholder = placeholder ?? t("fields.select");
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedLabel =
    options.find((opt) => opt.value === selected)?.label || "";

  const displayValue = isUserTyping ? query : selectedLabel;

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current !== event.target
      ) {
        setIsOpen(false);
        setQuery("");
        setIsUserTyping(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative w-full ${wrapperClassName}`} ref={dropdownRef}>
      <div className="relative w-full flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsUserTyping(true);
            setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setIsUserTyping(true);
          }}
          placeholder={shownPlaceholder}
          className={`w-full px-3 py-2 text-sm border rounded-md bg-ui-surface text-ui-text focus:outline-none focus:ring-2 focus:ring-brand ${inputClassName}`}
        />
        <FaChevronDown
          className={`absolute right-3 text-ui-text-muted w-3 h-3 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.ul
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`absolute pl-0 w-full bg-ui-surface border border-ui-border rounded-md shadow-md mt-1 z-50 max-h-60 overflow-y-auto ${dropdownClassName}`}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className={`px-3 py-2 text-sm text-ui-text cursor-pointer hover:bg-ui-surface-sunken transition ${optionClassName} ${
                    selected === option.value ? selectedOptionClassName : ""
                  }`}
                  onClick={() => {
                    setSelected(option.value);
                    setQuery("");
                    setIsOpen(false);
                    setIsUserTyping(false);
                  }}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li
                className={`px-3 py-2 text-sm text-ui-text-subtle ${noResultsClassName}`}
              >
                {t("empty.noResults")}
              </li>
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AutoComplete;
